import { IoSearch, IoClose } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { SEARCH } from "../../constants/RoutesUrl";
import { debounce } from "lodash";
import { useContext, useState, useEffect, useRef, useCallback } from "react";
import { Language } from "../../App";
import RecentSearchIcon from "../../assets/images/icons/recent-search.svg";
import SearchIcon from "../../assets/images/icons/search.svg";
import "./SearchInput.css";
import {
  clearRecentSearches,
  getSearchAgainstKeywordApi,
} from "../../services/searchApis";
import { useMutation, useQuery } from "react-query";
import ClearSearchPopup from "./ClearSearchPopup";
import { toast } from "react-toastify";
import MixpanelManager from "../../constants/MixpanelManager";
import { MixpanelEvents } from "../../constants/MixpanelEvents";

export default function SearchInput({topSearches=[],recentSearches=[],setRecentSearches}) {
  const { lang } = useContext(Language);
  const navigate = useNavigate();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [search, setSearch] = useState("");
  const [isClearing, setIsClearing] = useState(false);
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const query = useLocation().search;
  const searchKeyword = decodeURIComponent(query.split("=")[1] || "");

  // Check if device is mobile/tablet
  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isMobileWidth = width <= 991; // Based on your CSS class d-none-991
      
      setIsMobile(isMobileDevice || isMobileWidth);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Handle mobile search toggle
  const handleMobileSearchToggle = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
    
    if (!isMobileSearchOpen) {
      setIsActive(true);
       MixpanelManager.track(MixpanelEvents.globalSearch, {
      user_id: JSON.parse(localStorage.getItem("SignupData"))?.data?.id || "",
    });
      // Focus input after state update
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      setIsActive(false);
      setInputValue("");
      setSearch("");
    }
  };

  const togglePopup = () => setIsPopupOpen(!isPopupOpen);

  const mutation = useMutation(clearRecentSearches, {
    onMutate: () => {
      setIsClearing(true);
    },
    onSuccess: () => {
      setIsClearing(false); 
      togglePopup();
      setRecentSearches([])
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('keyword');
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      toast.success("Recent searches cleared successfully!");
    },
    onError: (error) => {
      console.error(error.message);
      alert("Failed to clear recent searches.");
    },
  });

  const handleClear = () => {
    mutation.mutate();
  };

  const { data: searchAgainstKeyword, isLoading } = useQuery({
    queryKey: ["search-against-keyword", search, inputValue],
    queryFn: () => getSearchAgainstKeywordApi(search ? search : ""),
    enabled: !!search || !!inputValue,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const keyword = searchKeyword && searchKeyword === "undefined" ? "" : searchKeyword;
    setInputValue(keyword);
    setSearch(keyword);
  }, [searchKeyword]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target)
      ) {
        setIsActive(false);
        if (isMobile) {
          setIsMobileSearchOpen(false);
        }
      }
    };
  
    if (!isPopupOpen && (isActive || isMobileSearchOpen)) {
      document.addEventListener("mousedown", handleClickOutside);
    }
  
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopupOpen, isActive, isMobileSearchOpen, isMobile]);

  const handleSearchClick = () => {
    if (inputValue) {
      setSearch(inputValue);
      setIsActive(false);
      if (isMobile) {
        setIsMobileSearchOpen(false);
      }
      navigate(SEARCH + "?keyword=" + encodeURIComponent(inputValue));
    }
  };

  const handleInputClick = () => {
  setIsActive(true);
  MixpanelManager.track(MixpanelEvents.globalSearch, {
    user_id: JSON.parse(localStorage.getItem("SignupData"))?.data?.id || "",
  });
};

  const debouncedSetInputValue = useCallback(
    debounce((value) => {
      setSearch(value);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSetInputValue(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue) {
      setSearch(inputValue);
      setIsActive(false);
      if (isMobile) {
        setIsMobileSearchOpen(false);
      }
      navigate(SEARCH + "?keyword=" + encodeURIComponent(inputValue));
    }
  };

  const handleSuggestionClick = (value) => {
    setInputValue(value);
    setSearch(value);
    setIsActive(false);
    if (isMobile) {
      setIsMobileSearchOpen(false);
    }
    navigate(SEARCH + "?keyword=" + encodeURIComponent(value));
  };

  // Mobile view - just search icon
  if (isMobile && !isMobileSearchOpen) {
    return (
      <div className="mobile-search-icon-container">
        <IoSearch
          className="mobile-search-icon"
          size={40}
          color="#666"
          onClick={handleMobileSearchToggle}
          style={{
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        />
      </div>
    );
  }

  // Mobile search overlay
  if (isMobile && isMobileSearchOpen) {
    return (
      <>
        <div 
          className="mobile-search-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
          }}
          onClick={handleMobileSearchToggle}
        />
        <div 
          className="mobile-search-container"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            zIndex: 9999,
            padding: '16px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxHeight: '100vh',
            overflowY: 'auto',
          }}
          ref={inputRef}
        >
          <div className="mobile-search-header" style={{ marginBottom: '16px' }}>
            <div className="position-relative" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IoSearch
                className="position-absolute"
                size={20}
                color="grey"
                onClick={handleSearchClick}
                style={{
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  zIndex: 1,
                }}
              />
              <form onSubmit={handleSubmit} style={{ flex: 1 }}>
                <input
                  ref={inputRef}
                  type="text"
                  style={{
                    backgroundColor: "#F0F7FA",
                    border: '1px solid #ddd',
                    borderRadius: '25px',
                    padding: '12px 16px 12px 44px',
                    fontSize: '16px',
                    width: '100%',
                    outline: 'none',
                  }}
                  placeholder={lang?.search_medical_radar}
                  value={inputValue}
                  onChange={handleSearchChange}
                  autoFocus
                />
              </form>
              <IoClose
                size={24}
                color="#666"
                onClick={handleMobileSearchToggle}
                style={{
                  cursor: 'pointer',
                  padding: '4px',
                  minWidth: '32px',
                }}
              />
            </div>
          </div>

          {/* Search Results - Mobile */}
          <div className="mobile-search-results">
            {search === "" && (
              <>
                {recentSearches?.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px' }}>
                      <h6 className="fs-14 roboto-medium text-muted" style={{ margin: 0 }}>
                        {lang?.your_recent_searches}:
                      </h6>
                      <span
                        className="fs-14 text-muted cursor roboto-medium"
                        onClick={() => setIsPopupOpen(true)}
                        style={{ cursor: 'pointer' }}
                      >
                        {lang.clear}
                      </span>
                    </div>
                    <div>
                      {recentSearches
                        ?.slice(0, 5)
                        .map((value, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center gap-2 cursor"
                            onClick={() => handleSuggestionClick(value)}
                            style={{
                              padding: '12px 0',
                              borderBottom: '1px solid #f0f0f0',
                              cursor: 'pointer',
                            }}
                          >
                            <img src={RecentSearchIcon} alt="recent-search" width={20} />
                            <h5 className="mb-0 fs-14 roboto-bold" style={{ margin: 0 }}>{value}</h5>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <div>
                  <h6 className="fs-14 roboto-medium text-muted" style={{ marginBottom: '12px' }}>
                    {lang?.popular_searches}
                  </h6>
                  <div>
                    {topSearches
                      ?.slice(0, 5)
                      .map((value, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center gap-2 cursor"
                          onClick={() => handleSuggestionClick(value)}
                          style={{
                            padding: '12px 0',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: 'pointer',
                          }}
                        >
                          <img src={SearchIcon} alt="search" width={20} />
                          <h5 className="mb-0 fs-14 roboto-bold" style={{ margin: 0 }}>{value}</h5>
                        </div>
                      ))}
                  </div>
                </div>
              </>
            )}

            {search && (
              <div>
                {isLoading ? (
                  <div className="w-100 p-3">
                    <h5 className="mb-0 fs-14 roboto-bold text-center text-muted">
                      Searching...
                    </h5>
                  </div>
                ) : searchAgainstKeyword?.data?.length > 0 ? (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      {searchAgainstKeyword?.data
                        ?.slice(0, 10)
                        .map((value, index) => (
                          <div
                            key={index}
                            className="d-flex align-items-center gap-2 cursor"
                            onClick={() => handleSuggestionClick(value)}
                            style={{
                              padding: '12px 0',
                              borderBottom: '1px solid #f0f0f0',
                              cursor: 'pointer',
                            }}
                          >
                            <img src={SearchIcon} alt="search" width={20} />
                            <h5 className="mb-0 fs-14 roboto-bold" style={{ margin: 0 }}>{value}</h5>
                          </div>
                        ))}
                    </div>
                    <div className="d-flex justify-content-center">
                      <h5
                        className="fs-14 roboto-bold cursor text-primary"
                        onClick={handleSearchClick}
                        style={{ 
                          margin: '16px 0',
                          cursor: 'pointer',
                          padding: '8px 16px',
                          border: '1px solid #007bff',
                          borderRadius: '20px',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {lang?.see_all_results}
                      </h5>
                    </div>
                  </>
                ) : (
                  <div className="w-100 p-3">
                    <h5 className="mb-0 fs-14 roboto-bold text-center text-muted">
                      {lang.no_results_found}
                    </h5>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <ClearSearchPopup isClearing={isClearing} isOpen={isPopupOpen} toggle={togglePopup} onConfirm={handleClear} />
      </>
    );
  }

  // Desktop version (original functionality)
  return (
    <>
      {isActive && <div className="overlay" style={{ zIndex: "1" }}></div>}
      <div className="position-relative d-none-991 w-100" ref={inputRef}>
        <IoSearch
          className="position-absolute m-auto top-0 bottom-0 ms-lg-3 ms-md-2 ms-sm-2 ms-1 cursor"
          size={20}
          color="grey"
          onClick={handleSearchClick}
        />
        <form onSubmit={handleSubmit}>
          <input
            onClick={handleInputClick}
            type="text"
            style={{backgroundColor:"#F0F7FA"}}
            placeholder={lang?.search_medical_radar}
            className={`search-input ${isActive ? "active" : ""}`}
            value={inputValue}
            onChange={handleSearchChange}
          />
        </form>
        {isActive && search === "" && (
          <div className="search-dropdown">
            {recentSearches?.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center px-3 pt-3">
                  <h6 className="fs-14 roboto-medium text-muted">
                    {lang?.your_recent_searches}:
                  </h6>
                  <span
                    className="fs-14 text-muted cursor roboto-medium"
                    onClick={() => setIsPopupOpen(true)}
                  >
                    {lang.clear}
                  </span>
                </div>
                <div>
                  {recentSearches
                    ?.slice(0, 5)
                    .map((value, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center gap-2 cursor manue-hover px-3 py-2"
                        onClick={() => handleSuggestionClick(value)}
                      >
                        <img src={RecentSearchIcon} alt="recent-search" width={20} />
                        <h5 className="mb-0 fs-14 roboto-bold">{value}</h5>
                      </div>
                    ))}
                </div>
              </div>
            )}
            <div>
              <h6 className="px-3 pt-3 fs-14 roboto-medium text-muted">
                {lang?.popular_searches}
              </h6>
              <div>
                {topSearches
                  ?.slice(0, 5)
                  .map((value, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center gap-2 cursor px-3 py-2"
                      onClick={() => handleSuggestionClick(value)}
                    >
                      <img src={SearchIcon} alt="search" width={20} />
                      <h5 className="mb-0 fs-14 roboto-bold">{value}</h5>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
        {isActive && search && (
          <div className="search-dropdown">
            <div>
              {isLoading ? (
                <div className="w-100 p-3">
                  <h5 className="mb-0 fs-14 roboto-bold text-center text-muted">
                    Searching...
                  </h5>
                </div>
              ) : searchAgainstKeyword?.data?.length > 0 ? (
                <>
                  <div className="border-bottom">
                    {searchAgainstKeyword?.data
                      ?.slice(0, 10)
                      .map((value, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center gap-2 m-3 cursor"
                          onClick={() => handleSuggestionClick(value)}
                        >
                          <img src={SearchIcon} alt="search" width={20} />
                          <h5 className="mb-0 fs-14 roboto-bold">{value}</h5>
                        </div>
                      ))}
                  </div>
                  <div className="d-flex justify-content-center">
                    <h5
                      className="fs-14 roboto-bold cursor my-3 text-primary"
                      onClick={handleSearchClick}
                    >
                      {lang?.see_all_results}
                    </h5>
                  </div>
                </>
              ) : (
                <div className="w-100 p-3">
                  <h5 className="mb-0 fs-14 roboto-bold text-center text-muted">
                    {lang.no_results_found}
                  </h5>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ClearSearchPopup isClearing={isClearing} isOpen={isPopupOpen} toggle={togglePopup} onConfirm={handleClear} />
    </>
  );
}